import type { IInventoryRepository } from '@yawi/domain';
import type { PaginatedResponse } from '@yawi/domain';
import type {
  AssetCategory,
  CreateAssetCategoryDTO,
  UpdateAssetCategoryDTO,
  Asset,
  AssetParams,
  CreateAssetDTO,
  UpdateAssetDTO,
  AssetQRResponse,
  AssetMaintenance,
  MaintenanceParams,
  CreateMaintenanceDTO,
  Consumable,
  ConsumableParams,
  CreateConsumableDTO,
  UpdateConsumableDTO,
  StockMovement,
  StockMovementParams,
  StockInDTO,
  StockOutDTO,
  StockAdjustmentDTO,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

export class ApiInventoryRepository implements IInventoryRepository {
  private readonly assetsPath = 'v1/inventory/assets';
  private readonly consumablesPath = 'v1/inventory/consumables';
  private readonly categoriesPath = 'v1/inventory/categories';

  // ========== ASSET CATEGORIES ==========

  async getCategories(): Promise<AssetCategory[]> {
    const client = getApiClient();
    return await client.get<AssetCategory[]>(this.categoriesPath);
  }

  async createCategory(data: CreateAssetCategoryDTO): Promise<AssetCategory> {
    const client = getApiClient();
    return await client.post<AssetCategory>(this.categoriesPath, data);
  }

  async updateCategory(id: string, data: UpdateAssetCategoryDTO): Promise<AssetCategory> {
    const client = getApiClient();
    return await client.put<AssetCategory>(`${this.categoriesPath}/${id}`, data);
  }

  async deleteCategory(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${this.categoriesPath}/${id}`);
  }

  // ========== ASSETS CRUD ==========

  async getAssets(params?: AssetParams): Promise<PaginatedResponse<Asset>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.search) queryParams.set('search', params.search);
    if (params?.categoryId) queryParams.set('categoryId', params.categoryId);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.location) queryParams.set('location', params.location);
    if (params?.maintenanceDue !== undefined) queryParams.set('maintenanceDue', String(params.maintenanceDue));
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = `${this.assetsPath}${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<Asset>>(url);
  }

  async getAssetById(id: string): Promise<Asset> {
    const client = getApiClient();
    return await client.get<Asset>(`${this.assetsPath}/${id}`);
  }

  async createAsset(data: CreateAssetDTO): Promise<Asset> {
    const client = getApiClient();
    return await client.post<Asset>(this.assetsPath, data);
  }

  async updateAsset(id: string, data: UpdateAssetDTO): Promise<Asset> {
    const client = getApiClient();
    return await client.put<Asset>(`${this.assetsPath}/${id}`, data);
  }

  async deleteAsset(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${this.assetsPath}/${id}`);
  }

  async getAssetQR(id: string): Promise<AssetQRResponse> {
    const client = getApiClient();
    return await client.get<AssetQRResponse>(`${this.assetsPath}/${id}/qr`);
  }

  async getMaintenanceDueAssets(): Promise<Asset[]> {
    const client = getApiClient();
    return await client.get<Asset[]>(`${this.assetsPath}/maintenance-due`);
  }

  // ========== ASSET MAINTENANCE ==========

  async getMaintenanceHistory(assetId: string, params?: MaintenanceParams): Promise<PaginatedResponse<AssetMaintenance>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.type) queryParams.set('type', params.type);
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);

    const queryString = queryParams.toString();
    const url = `${this.assetsPath}/${assetId}/maintenance${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<AssetMaintenance>>(url);
  }

  async createMaintenance(assetId: string, data: CreateMaintenanceDTO): Promise<AssetMaintenance> {
    const client = getApiClient();
    return await client.post<AssetMaintenance>(
      `${this.assetsPath}/${assetId}/maintenance`,
      data
    );
  }

  // ========== CONSUMABLES CRUD ==========

  async getConsumables(params?: ConsumableParams): Promise<PaginatedResponse<Consumable>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.search) queryParams.set('search', params.search);
    if (params?.location) queryParams.set('location', params.location);
    if (params?.lowStock !== undefined) queryParams.set('lowStock', String(params.lowStock));
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = `${this.consumablesPath}${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<Consumable>>(url);
  }

  async getConsumableById(id: string): Promise<Consumable> {
    const client = getApiClient();
    return await client.get<Consumable>(`${this.consumablesPath}/${id}`);
  }

  async createConsumable(data: CreateConsumableDTO): Promise<Consumable> {
    const client = getApiClient();
    return await client.post<Consumable>(this.consumablesPath, data);
  }

  async updateConsumable(id: string, data: UpdateConsumableDTO): Promise<Consumable> {
    const client = getApiClient();
    return await client.put<Consumable>(`${this.consumablesPath}/${id}`, data);
  }

  async deleteConsumable(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${this.consumablesPath}/${id}`);
  }

  async getLowStockConsumables(): Promise<Consumable[]> {
    const client = getApiClient();
    return await client.get<Consumable[]>(`${this.consumablesPath}/low-stock`);
  }

  // ========== STOCK MOVEMENTS ==========

  async getStockMovements(consumableId: string, params?: StockMovementParams): Promise<PaginatedResponse<StockMovement>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.type) queryParams.set('type', params.type);
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);

    const queryString = queryParams.toString();
    const url = `${this.consumablesPath}/${consumableId}/movements${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<StockMovement>>(url);
  }

  async stockIn(consumableId: string, data: StockInDTO): Promise<StockMovement> {
    const client = getApiClient();
    return await client.post<StockMovement>(
      `${this.consumablesPath}/${consumableId}/stock-in`,
      data
    );
  }

  async stockOut(consumableId: string, data: StockOutDTO): Promise<StockMovement> {
    const client = getApiClient();
    return await client.post<StockMovement>(
      `${this.consumablesPath}/${consumableId}/stock-out`,
      data
    );
  }

  async adjustStock(consumableId: string, data: StockAdjustmentDTO): Promise<StockMovement> {
    const client = getApiClient();
    return await client.post<StockMovement>(
      `${this.consumablesPath}/${consumableId}/adjust`,
      data
    );
  }
}
